import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ArrowRight, AlertTriangle, Gauge, Droplets, Link2,
  CheckCircle2, ShieldCheck, TrendingDown,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

/* bold-parser */
const B = ({ t, cls = "text-ink font-semibold" }) => (
  <>
    {t.split(/(\*\*[^*]+\*\*)/g).map((s, i) =>
      s.startsWith("**") && s.endsWith("**")
        ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong>
        : <span key={i}>{s}</span>
    )}
  </>
);

const PAINS = [
  "**Mat deflection** lets heavy machinery **sink** into soft subgrade.",
  "**Subgrade failure** delays **mechanical trades** and the critical path.",
  "**Porous mats** absorb and **leach hydrocarbons** into sensitive soils.",
  "Contaminated ground triggers **environmental fines** and remediation.",
  "**Independent mat shifting** breaks the continuous load path.",
];

const SOLUTIONS = [
  {
    icon: Gauge,
    title: "Compressive Strength & Load Distribution",
    rows: [
      "**High-psi** load-bearing capacity.",
      "Spreads point loads over **saturated, low-CBR soils.**",
      "Prevents **localized subgrade failure.**",
      "Controlled **deflection** under crane & tracked loads.",
    ],
  },
  {
    icon: Droplets,
    title: "Zero-Absorption Material",
    rows: [
      "**Non-porous** engineered polymer.",
      "Blocks **moisture & chemical leaching.**",
      "**100% certified** decontamination washdowns.",
      "**Zero** hydrocarbon retention.",
    ],
  },
  {
    icon: Link2,
    title: "Mechanical Connection Integrity",
    rows: [
      "Locking system ties the **full mat matrix.**",
      "Distributes **lateral stresses** across the field.",
      "**No independent** mat shifting.",
      "Continuous, **monolithic load path.**",
    ],
  },
];

const OUTCOMES = [
  { icon: ShieldCheck, v: "Certified", l: "environmental compliance" },
  { icon: TrendingDown, v: "Protected", l: "subgrade — structural predictability" },
  { icon: CheckCircle2, v: "Mitigated", l: "liability for the asset owner" },
];

export default function EngineeringBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Engineering Brief · One-Page</span>
            <a href="/big-bad-mats-engineering-brief.pdf" download data-testid="download-engineering-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-engineering-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="engineering-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">For the Lead Civil Engineer · EPCM Firms</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[31px]">
                Prevent Subgrade Failure. Engineer Structural Predictability.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                A load-rated ground interface for <span className="text-flame font-semibold">saturated, low-CBR subgrades</span> — with a <span className="font-semibold text-ink">non-porous, zero-leach</span> surface that protects sensitive ecosystems and the asset owner's liability.
              </p>
            </div>
            <div className="w-[32%] bg-ink text-white p-3">
              <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">Engineering envelope</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">600 psi</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">bearing capacity</div>
                </div>
                <div>
                  <div className="font-display font-extrabold text-flame text-[22px] leading-none">0%</div>
                  <div className="text-[7.5px] uppercase tracking-wider text-white/50">fluid absorption</div>
                </div>
              </div>
            </div>
          </header>

          {/* body: pain (left) + solutions (right) */}
          <div className="flex-1 grid grid-cols-5 gap-4 pt-4 min-h-0">
            {/* pain */}
            <div className="col-span-2 border border-border flex flex-col">
              <div className="bg-charcoal text-white px-4 py-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-flame" />
                <span className="font-display font-bold text-[12px] uppercase tracking-wider">Engineering Vulnerabilities</span>
              </div>
              <ul className="p-4 space-y-3 flex-1 flex flex-col justify-center">
                {PAINS.map((p, i) => (
                  <li key={p} className="flex items-start gap-2.5 text-charcoal text-[11px] leading-snug">
                    {i === 2
                      ? <Droplets className="h-4 w-4 text-flame flex-shrink-0 mt-0.5" />
                      : <span className="mt-1.5 h-1.5 w-1.5 bg-charcoal/40 flex-shrink-0" />}
                    <span><B t={p} cls="text-ink font-semibold" /></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* solutions */}
            <div className="col-span-3 grid grid-rows-3 gap-3">
              {SOLUTIONS.map((s) => (
                <div key={s.title} className="border-2 border-flame flex flex-col">
                  <div className="bg-flame text-white px-4 py-1.5 flex items-center gap-2">
                    <s.icon className="h-4 w-4" />
                    <span className="font-display font-bold text-[11.5px] uppercase tracking-wider">{s.title}</span>
                  </div>
                  <ul className="px-4 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5 flex-1 content-center">
                    {s.rows.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-charcoal text-[10.5px] leading-snug">
                        <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                        <span><B t={x} /></span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* engineering outcome breakout */}
          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-flame" />
              <span className="font-display font-bold text-[13px] uppercase tracking-wider">The Engineering Outcome</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {OUTCOMES.map((o) => (
                <div key={o.l} className="bg-white/5 border border-white/10 px-4 py-2.5 flex items-center gap-3">
                  <o.icon className="h-6 w-6 text-flame flex-shrink-0" />
                  <div>
                    <div className="font-display font-extrabold text-flame text-[18px] leading-none">{o.v}</div>
                    <div className="text-white/60 text-[9.5px] mt-0.5">{o.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA footer */}
          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px] leading-tight">Specify with confidence — pull the full engineering package.</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 print:hidden">
              <Link to="/big-bad-mats-datasheet" className="inline-flex items-center gap-1.5 bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors">
                Technical Spec Sheet <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/request-quote" className="inline-flex items-center gap-1.5 border border-border text-ink px-4 py-2.5 font-semibold text-[11px] hover:border-flame hover:text-flame transition-colors">
                Request CAD &amp; FEA Data
              </Link>
            </div>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Load ratings, CBR performance &amp; FEA outputs are representative — confirm certified values &amp; project-specific geotechnical parameters during design review. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
